import { useEffect, useState } from 'react';

import { fetchAccountBadge } from '../lib/clientAccounts';
import { accountFromRow } from '../lib/crm/accountRow';
import { logWarn } from '../lib/logger';
import { supabase } from '../lib/supabase';
import type { CRMAccount } from '../types/crm';
import type { ClientBadge } from '../utils/clientStatus';

export interface AccountSheetHead {
  account: CRMAccount | null;
  /** Null while unknown or unreadable: the sheet shows no role rather than a wrong one. */
  badge: ClientBadge | null;
  /** The group the account belongs to, by its key. */
  parent: { id: string; name: string } | null;
  loading: boolean;
  /** No such account, or deleted: the sheet says so. */
  missing: boolean;
  /** The account could not be read (network, rights): the sheet says that, not « introuvable ». */
  error: string | null;
}

const EMPTY: AccountSheetHead = { account: null, badge: null, parent: null, loading: false, missing: false, error: null };

/**
 * The head of an account's working sheet: the account, who it is for us (`v_account_role`) and its
 * group. Read when the sheet opens; the rest of the sheet comes from `useAccountDetailData`. An
 * account that is not there (deleted: `crm_accounts` has no soft delete) is missing.
 */
export function useAccountSheet(accountId: string | null): AccountSheetHead {
  const [state, setState] = useState<AccountSheetHead>(EMPTY);

  useEffect(() => {
    if (!accountId) {
      setState(EMPTY);
      return;
    }
    let alive = true;
    setState({ ...EMPTY, loading: true });
    void (async () => {
      const [row, badge] = await Promise.all([
        supabase.from('crm_accounts').select('*').eq('id', accountId).maybeSingle(),
        fetchAccountBadge(accountId),
      ]);
      if (!alive) return;
      if (row.error) {
        logWarn('crm', 'account sheet: account unreadable', { accountId, error: row.error.message });
        setState({ ...EMPTY, error: row.error.message });
        return;
      }
      const account = row.data ? accountFromRow(row.data) : null;
      setState({ account, badge, parent: null, loading: false, missing: !account, error: null });
      if (!account?.parent_account_id) return;
      const { data, error } = await supabase.from('crm_accounts').select('id, name').eq('id', account.parent_account_id).maybeSingle();
      if (error) logWarn('crm', 'account sheet: group unreadable', { accountId, error: error.message });
      if (alive && data) setState((s) => (s.account?.id === accountId ? { ...s, parent: { id: data.id, name: data.name } } : s));
    })();
    return () => {
      alive = false;
    };
  }, [accountId]);

  return state;
}
